import { useEffect, useState, useMemo, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSchool,
  faUserTie,
  faMagnifyingGlass,
  faCircleUser,
  faBuildingColumns,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { saveLogin } from "../../redux/slices/mainDataSlice";
import Tooltip from "../../components/Tooltip";

export default function FindTeachers() {
  const isAuth = useSelector((state) => state.main.isAuthenticated);
  const schoolsData = useSelector((state) => state.main.data?.schoolData);
  const teachersData = useSelector((state) => state.main.data?.allteachers);
  const thawabetData = useSelector((state) => state.main.data?.thawabetData);
  const navigate = useNavigate();
  const dispatch = useDispatch();


  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  const [filter, setFilter] = useState("");
  const [debouncedFilter, setDebouncedFilter] = useState("");
  const [activeTab, setActiveTab] = useState("teachers"); // Set default to employee as requested
  const [visibleCount, setVisibleCount] = useState(50);
  const debounceTimer = useRef(null);

  // Debounce: الكتابة فورية، والفلترة بعد توقف الكتابة بـ 300ms
  const handleFilterChange = (event) => {
    const searchText = event.target.value;
    setFilter(searchText);
    setVisibleCount(50);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedFilter(searchText);
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  // Helper function for multi-word search with AND logic
  const matchesAllWords = (text, searchFilter) => {
    if (!searchFilter || !searchFilter.trim()) return true;
    const words = searchFilter.trim().toLowerCase().split(/\s+/).filter(w => w.length > 0);
    const lowerText = (text || "").toLowerCase();
    return words.every(word => lowerText.includes(word));
  };

  const filteredSchools = useMemo(() => {
    if (!debouncedFilter.trim()) return [];
    return schoolsData?.filter((school) =>
      matchesAllWords(school.schoolname, debouncedFilter)
    ) || [];
  }, [schoolsData, debouncedFilter]);

  const filteredTeachers = useMemo(() => {
    const q = debouncedFilter.trim();
    if (!q) return [];
    return teachersData?.filter((teacher) =>
      matchesAllWords(teacher.emp_name, q) ||
      String(teacher.emp_id || "").includes(q)
    ) || [];
  }, [teachersData, debouncedFilter]);

  // خريطة المدرسة -> المديرية محسوبة مرة واحدة
  const schoolDirectorateMap = useMemo(() => {
    const map = {};
    thawabetData?.forEach((t) => {
      const key = t.school_rgmwtni || t.rgmw;
      if (key && !map[key]) {
        map[key] = t.AlModeeria_name_a || t.Modeeria_name_a || "غير محدد";
      }
    });
    return map;
  }, [thawabetData]);

  // خريطة rgmw -> اسم المدرسة محسوبة مرة واحدة
  const schoolNameMap = useMemo(() => {
    const map = {};
    schoolsData?.forEach((s) => {
      if (s.rgmw && !map[s.rgmw]) map[s.rgmw] = s.schoolname;
    });
    return map;
  }, [schoolsData]);

  useEffect(() => {
    if (!isAuth) {
      const storedUserCredentials = JSON.parse(
        localStorage.getItem("userCredentials")
      );
      if (storedUserCredentials) {
        dispatch(saveLogin(storedUserCredentials));
      } else {
        navigate("/");
      }
    }
  }, [isAuth, dispatch, navigate]);

  const handleSchoolClick = (rgmw) => {
    navigate(`/schools/${rgmw}`);
  };

  const resultCount =
    activeTab === "schools"
      ? filteredSchools?.length
      : filteredTeachers?.length;

  return (
    <div className="search-page-container anim-fade-in">
      <div className="management-card search-main-card">
        <div className="search-header">
          <h3 className="card-title">
            <FontAwesomeIcon icon={faSearch} style={{ marginLeft: "10px" }} />
            مركز البحث عن مدرسة او موظف
          </h3>
          <div className="search-tabs">
            <button
              className={`nav-square-btn ${
                activeTab === "teachers" ? "active" : ""
              }`}
              onClick={() => {
                setActiveTab("teachers");
                setFilter("");
                setDebouncedFilter("");
                setVisibleCount(50);
              }}
            >
              <FontAwesomeIcon icon={faUserTie} />
              <span>البحث عن موظف</span>
            </button>
            <button
              className={`nav-square-btn ${
                activeTab === "schools" ? "active" : ""
              }`}
              onClick={() => {
                setActiveTab("schools");
                setFilter("");
                setDebouncedFilter("");
                setVisibleCount(50);
              }}
            >
              <FontAwesomeIcon icon={faSchool} />
              <span>البحث عن مدرسة</span>
            </button>
          </div>
        </div>

        <div className="premium-search-box">
          <div className="input-wrapper">
            <FontAwesomeIcon icon={faMagnifyingGlass} className="search-icon" />
            <input
              type="text"
              placeholder={
                activeTab === "schools"
                  ? "ابحث عن اسم المدرسة..."
                  : "ابحث بالاسم أو رقم الهوية..."
              }
              value={filter}
              onChange={handleFilterChange}
              autoFocus
            />
            {filter && (
              <div className="results-badge">{resultCount || 0} نتيجة</div>
            )}
          </div>
        </div>

        <div className="search-results-viewport">
          {filter === "" ? (
            <div className="empty-state">
              <div className="empty-icon">
                <FontAwesomeIcon
                  icon={
                    activeTab === "schools" ? faBuildingColumns : faCircleUser
                  }
                />
              </div>
              <p>
                ابدأ بكتابة اسم {activeTab === "schools" ? "المدرسة" : "الموظف"}{" "}
                للبحث عنه
              </p>
            </div>
          ) : (
            <div className="results-list">
              <>
                {activeTab === "schools" ? (
                  filteredSchools?.length > 0 ? (
                    <>
                    <div className="table-wrapper">
                      <table className="excel-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>اسم المدرسة</th>
                              <th>الرقم الوطني</th>
                              <th>المديرية</th>
                              <th>إجراءات</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredSchools.slice(0, visibleCount).map((school, index) => (
                              <tr key={school.rgmw}>
                                <td data-label="#">{index + 1}</td>
                                <td data-label="name">{school.schoolname}</td>
                                <td data-label="الرقم الوطني">{school.rgmw}</td>
                                <td data-label="المديرية">
                                  {schoolDirectorateMap[school.rgmw] || "غير محدد"}
                                </td>
                                <td data-label="actions">
                                  <Tooltip content="عرض التفاصيل">
                                    <button
                                      className="btn-table btn-table--view"
                                      onClick={() =>
                                        handleSchoolClick(school.rgmw)
                                      }
                                   
                                    >
                                      <FontAwesomeIcon icon={faSchool} />
                                    </button>
                                  </Tooltip>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {filteredSchools.length > visibleCount && (
                        <button
                          className="show-more-btn"
                          onClick={() => setVisibleCount((c) => c + 50)}
                        >
                          عرض المزيد ({filteredSchools.length - visibleCount} متبقي)
                        </button>
                      )}
                    </>
                    ) : (
                      <div className="no-results">لا توجد مدارس بهذا الاسم</div>
                    )
                  ) : filteredTeachers?.length > 0 ? (
                    <>
                    <div className="table-wrapper">
                      <table className="excel-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>اسم الموظف</th>
                            <th>المدرسة</th>
                            <th>رقم الهوية</th>
                            <th>رقم الهاتف</th>
                            <th>إجراءات</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredTeachers.slice(0, visibleCount).map((teacher, index) => (
                            <tr key={teacher.emp_id}>
                              <td data-label="#">{index + 1}</td>
                              <td data-label="name">{teacher.emp_name}</td>
                              <td data-label="المدرسة">
                                {schoolNameMap[teacher.rgmw] || "مدرسة غير معروفة"}
                              </td>
                              <td data-label="رقم الهوية">{teacher.emp_id || "—"}</td>
                              <td data-label="رقم الهاتف">{teacher.phone1 || "—"}</td>
                              <td data-label="actions">
                                <Tooltip content="تعديل الموظف">
                                  <button
                                    className="btn-table btn-table--edit"
                                    onClick={() =>
                                      navigate(`/editEmployee/${teacher.emp_id}`)
                                    }
                                 
                                  >
                                    <FontAwesomeIcon icon={faUserTie} />
                                  </button>
                                </Tooltip>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {filteredTeachers.length > visibleCount && (
                      <button
                        className="show-more-btn"
                        onClick={() => setVisibleCount((c) => c + 50)}
                      >
                        عرض المزيد ({filteredTeachers.length - visibleCount} متبقي)
                      </button>
                    )}
                    </>
                  ) : (
                    <div className="no-results">لا يوجد موظف بهذا الاسم</div>
                  )}
              </>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .search-page-container {
            width: 100%;
            height: calc(100vh - 120px);
            display: flex;
            justify-content: center;
            padding-top: 20px;
        }

        .search-main-card {
            width: 100%;
            max-width: 100%; /* توسعة لتناسب الصفحة بالكامل */
            display: flex;
            flex-direction: column;
            gap: 20px;
            height: auto;
            border: none;
            box-shadow: none;
            background: transparent;
        }

        .search-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 1px solid var(--border);
            padding-bottom: 0px;
            flex-wrap: wrap;
            gap: 15px;
            margin-bottom: 25px;
        }

        .search-tabs {
            display: flex;
            gap: 4px;
            background: var(--panel-2);
            border: 1px solid var(--border);
            border-radius: 6px;
            padding: 4px;
            width: 100%;
        }

        .nav-square-btn {
            flex: 1;
            background: transparent;
            border: none;
            color: var(--muted);
            padding: 10px 14px;
            cursor: pointer;
            transition: all 0.2s;
            font-family: "Cairo", sans-serif;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            font-size: 14px;
            border-radius: 6px;
            white-space: nowrap;
        }

        .nav-square-btn:hover {
            color: var(--accent);
        }

        .nav-square-btn.active {
            background: var(--panel);
            color: var(--accent);
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .nav-square-btn svg {
            font-size: 16px;
        }

        .premium-search-box {
            position: relative;
        }

        .input-wrapper {
            position: relative;
            display: flex;
            align-items: center;
        }

        .input-wrapper input {
            width: 100%;
            height: 54px;
            background: var(--panel-2);
            border: 1px solid var(--border);
            border-radius: 16px;
            padding: 0 50px;
            color: var(--text);
            font-family: 'Cairo', sans-serif;
            font-size: 16px;
            outline: none;
            transition: all 0.3s;
        }

        .input-wrapper input:focus {
            border-color: var(--accent);
            box-shadow: 0 0 0 4px color-mix(in srgb, var(--accent) 10%, transparent);
            background: color-mix(in srgb, var(--panel-2) 95%, white 2%);
        }

        .search-icon {
            position: absolute;
            right: 18px;
            color: var(--muted);
            font-size: 18px;
        }

        .results-badge {
            position: absolute;
            left: 15px;
            background: color-mix(in srgb, var(--accent) 15%, transparent);
            color: var(--accent);
            padding: 4px 12px;
            border-radius: 20px;
            font-size: 12px;
            font-weight: 500;
        }

        .search-results-viewport {
            flex: 1;
            overflow-y: auto;
            min-height: 300px;
            padding-right: 5px;
        }

        .empty-state {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 60px 0;
            color: var(--muted);
            opacity: 0.6;
        }

        .empty-icon {
            font-size: 60px;
            margin-bottom: 15px;
        }

        .results-list {
            display: flex;
            flex-direction: column;
            gap: 10px;
        }

        .table-wrapper {
            width: 100%;
            overflow-x: auto;
            background: var(--panel);
            border-radius: 12px;
            border: 1px solid var(--border);
            padding: 2px;
            box-sizing: border-box;
        }

        .excel-table {
            width: 100%;
            border-collapse: collapse;
            text-align: right;
            font-size: 13px;
            table-layout: auto;
            font-family: "Cairo", sans-serif;
        }

        .excel-table th {
            background: var(--panel-2);
            padding: 12px 10px;
            color: var(--accent);
            font-weight: 400;
            border-bottom: 1px solid var(--border);
            white-space: nowrap;
            font-size: 13px;
        }

        .excel-table td {
            padding: 10px;
            border-bottom: 1px solid var(--border);
            color: var(--text);
            white-space: nowrap;
            font-weight: 400;
        }

        .excel-table tbody tr:hover {
            background: color-mix(in srgb, var(--accent) 4%, transparent);
        }

        .btn-table {
            width: 30px;
            height: 30px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 6px;
            border: 1px solid var(--border);
            background: var(--panel-2);
            cursor: pointer;
            transition: 0.2s;
            font-size: 14px;
        }

        .btn-table--view { color: var(--accent); }
        .btn-table--view:hover { 
            background: var(--accent); 
            color: white; 
            border-color: var(--accent); 
        }

        .btn-table--edit { color: var(--accent); }
        .btn-table--edit:hover { 
            background: var(--accent); 
            color: white; 
            border-color: var(--accent); 
        }

        .no-results {
            text-align: center;
            padding: 40px;
            color: var(--muted);
        }

        .show-more-btn {
            display: block;
            width: 100%;
            margin-top: 12px;
            padding: 12px;
            background: var(--panel-2);
            border: 1px solid var(--border);
            border-radius: 6px;
            color: var(--accent);
            font-family: 'Cairo', sans-serif;
            font-size: 14px;
            cursor: pointer;
            transition: all 0.2s;
        }
        .show-more-btn:hover {
            background: var(--panel);
            border-color: var(--accent);
        }

        .loading-spinner {
            text-align: center;
            padding: 40px;
            color: var(--accent);
        }

        /* Scrollbar styling */
        .search-results-viewport::-webkit-scrollbar {
            width: 5px;
        }
        .search-results-viewport::-webkit-scrollbar-thumb {
            background: var(--border);
            border-radius: 10px;
        }

        /* بطاقات الهاتف */
        @media (max-width: 768px) {
            .search-tabs {
                margin-top: 4px;
            }
            .nav-square-btn {
                padding: 9px 10px;
                font-size: 13px;
                gap: 6px;
            }
            .table-wrapper {
                overflow: visible;
            }
            .excel-table thead {
                display: none;
            }
            .excel-table,
            .excel-table tbody {
                display: block;
                width: 100%;
            }
            .excel-table tbody {
                display: flex;
                flex-direction: column;
                gap: 10px;
            }
            .excel-table tr {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                background: var(--panel);
                border: 1px solid var(--border);
                border-radius: 6px;
                padding: 12px;
                gap: 0;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
            }
            .excel-table td {
                display: block;
                border: none;
                padding: 3px 0;
                white-space: normal;
                word-break: break-word;
            }
            /* رقم الترتيب مخفي */
            .excel-table td[data-label="#"] {
                display: none;
            }
            /* الاسم عنوان البطاقة */
            .excel-table td[data-label="name"] {
                flex: 1;
                min-width: 0;
                font-size: 15px;
                color: var(--text);
                line-height: 1.5;
                padding-inline-end: 8px;
            }
            /* زر الإجراء */
            .excel-table td[data-label="actions"] {
                flex-shrink: 0;
                margin-inline-start: auto;
            }
            /* باقي التفاصيل سطر كامل */
            .excel-table td[data-label]:not([data-label="#"]):not([data-label="name"]):not([data-label="actions"]) {
                flex-basis: 100%;
                width: 100%;
                font-size: 12px;
                color: var(--muted);
                display: flex;
                align-items: center;
                gap: 6px;
            }
            .excel-table td[data-label]:not([data-label="#"]):not([data-label="name"]):not([data-label="actions"])::before {
                content: attr(data-label) ":";
                color: var(--accent);
                font-size: 12px;
                flex-shrink: 0;
            }
            .btn-table {
                width: 34px;
                height: 34px;
            }
        }
      `}</style>
    </div>
  );
}
